import { useState, useEffect } from "react";
import { trpc } from "../../../trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { SearchableSelect } from "@/components/ui/searchable-select";

type MerchantData = {
  id: string;
  displayName: string;
  whatsappAccessToken: string;
  whatsappPhoneNumberId: string;
  type?: string;
  billingCycleDay?: number;
  minimumSubscriptionUsage?: number;
  unitPriceBelowMinimum?: number;
  unitPriceAboveMinimum?: number;
};

type StepMerchantProps = {
  onSubmit: (data: MerchantData, createNew: boolean) => Promise<void>;
  initialData: MerchantData | null;
  isLoading: boolean;
};

export const StepMerchant = ({ onSubmit, initialData, isLoading }: StepMerchantProps) => {
  const [mode, setMode] = useState<"select" | "create">("select");
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [formData, setFormData] = useState<MerchantData>({
    id: "",
    displayName: "",
    whatsappAccessToken: "",
    whatsappPhoneNumberId: "",
    billingCycleDay: 1,
    minimumSubscriptionUsage: 0,
    unitPriceBelowMinimum: 0,
    unitPriceAboveMinimum: 0,
  });

  const merchantsQuery = trpc.merchant.merchants.useQuery();
  const { data: merchants, isLoading: merchantsLoading, error: merchantsError } = merchantsQuery;

  useEffect(() => {
    if (initialData) {
      if (merchants?.some((m) => m.id === initialData.id)) {
        setMode("select");
        setSelectedMerchantId(initialData.id);
      } else {
        setMode("create");
        setFormData(initialData);
      }
    }
  }, [initialData, merchants]);

  const handleModeChange = (value: string) => {
    setMode(value as "select" | "create");
    if (value === "select") {
      setSelectedMerchantId("");
    } else {
      setFormData({
        id: "",
        displayName: "",
        whatsappAccessToken: "",
        whatsappPhoneNumberId: "",
        billingCycleDay: 1,
        minimumSubscriptionUsage: 0,
        unitPriceBelowMinimum: 0,
        unitPriceAboveMinimum: 0,
      });
    }
  };

  const handleInputChange = (field: keyof MerchantData, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (mode === "select") {
      const selectedMerchant = merchants?.find((m) => m.id === selectedMerchantId);
      if (selectedMerchant) {
        await onSubmit(
          {
            id: selectedMerchant.id,
            displayName: selectedMerchant.id,
            whatsappAccessToken: "",
            whatsappPhoneNumberId: "",
            billingCycleDay: selectedMerchant.billingCycleDay,
            minimumSubscriptionUsage: selectedMerchant.minimumSubscriptionUsage,
            unitPriceBelowMinimum: selectedMerchant.unitPriceBelowMinimum,
            unitPriceAboveMinimum: selectedMerchant.unitPriceAboveMinimum,
          },
          false
        );
      }
    } else {
      await onSubmit({ ...formData, type: "development" }, true);
    }
  };

  const isFormValid = () => {
    if (mode === "select") {
      return selectedMerchantId !== "";
    } else {
      return (
        formData.id &&
        formData.displayName &&
        formData.whatsappAccessToken &&
        formData.whatsappPhoneNumberId
      );
    }
  };

  if (merchantsLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <Spinner className="h-8 w-8" />
        <p>Cargando merchants...</p>
      </div>
    );
  }

  if (merchantsError) {
    return (
      <Alert status="error">
        Error loading merchants: {merchantsError.message}
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-lg font-semibold mb-4">Paso 1: Seleccionar o crear merchant</p>

        <RadioGroup value={mode} onValueChange={handleModeChange}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="select" id="select" />
              <Label htmlFor="select" className="font-normal cursor-pointer">
                Seleccionar merchant existente
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="create" id="create" />
              <Label htmlFor="create" className="font-normal cursor-pointer">
                Crear nuevo merchant
              </Label>
            </div>
          </div>
        </RadioGroup>
      </div>

      <hr />

      {mode === "select" ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="merchantSelect">Merchant *</Label>
          <SearchableSelect
            id="merchantSelect"
            value={selectedMerchantId}
            onValueChange={setSelectedMerchantId}
            placeholder="Selecciona un merchant..."
            searchPlaceholder="Buscar merchant..."
            items={merchants?.map((merchant) => ({
              value: merchant.id,
              label: `${merchant.id} (${merchant.type})`,
            })) || []}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="merchantId">ID *</Label>
            <Input
              id="merchantId"
              value={formData.id}
              onChange={(e) => handleInputChange("id", e.target.value)}
              placeholder="Ingresa el ID del merchant"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="merchantName">Nombre del merchant *</Label>
            <Input
              id="merchantName"
              value={formData.displayName}
              onChange={(e) => handleInputChange("displayName", e.target.value)}
              placeholder="Ingresa el nombre del merchant"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="whatsappToken">WhatsApp Access Token *</Label>
            <Input
              id="whatsappToken"
              value={formData.whatsappAccessToken}
              onChange={(e) => handleInputChange("whatsappAccessToken", e.target.value)}
              placeholder="Ingresa el token de acceso de WhatsApp"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="whatsappPhoneId">WhatsApp Phone Number ID *</Label>
            <Input
              id="whatsappPhoneId"
              value={formData.whatsappPhoneNumberId}
              onChange={(e) => handleInputChange("whatsappPhoneNumberId", e.target.value)}
              placeholder="Ingresa el ID del número de teléfono"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="billingCycleDay">Día del ciclo de facturación</Label>
            <Input
              id="billingCycleDay"
              type="number"
              min={1}
              max={31}
              value={formData.billingCycleDay}
              onChange={(e) => handleInputChange("billingCycleDay", parseInt(e.target.value) || 1)}
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="minSubscription">Uso mínimo de suscripción</Label>
            <Input
              id="minSubscription"
              type="number"
              min={0}
              value={formData.minimumSubscriptionUsage}
              onChange={(e) =>
                handleInputChange("minimumSubscriptionUsage", parseInt(e.target.value) || 0)
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="priceBelow">Precio unitario por debajo del mínimo</Label>
            <Input
              id="priceBelow"
              type="number"
              min={0}
              step={0.01}
              value={formData.unitPriceBelowMinimum}
              onChange={(e) =>
                handleInputChange("unitPriceBelowMinimum", parseFloat(e.target.value) || 0)
              }
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="priceAbove">Precio unitario por encima del mínimo</Label>
            <Input
              id="priceAbove"
              type="number"
              min={0}
              step={0.01}
              value={formData.unitPriceAboveMinimum}
              onChange={(e) =>
                handleInputChange("unitPriceAboveMinimum", parseFloat(e.target.value) || 0)
              }
            />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!isFormValid() || isLoading}>
          {isLoading ? "Procesando..." : "Continuar"}
        </Button>
      </div>
    </div>
  );
};
